// /academy/psicologia/:id — Lesson detail page. Renders structured
// `contenido` for the "7 errors" lesson and free-form text for the rest.
import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Brain, AlertCircle, ShieldCheck, Dumbbell } from "lucide-react";
import AcademyShell from "../components/academy/AcademyShell";
import { useLanguage } from "../i18n/LanguageContext";
import { getAcademyToolsI18n } from "../i18n/academy-tools";
import { findLeccionLang, getLeccionesPsicologia } from "../data/i18n-academy";

const ErrorBlock = ({ item, T }) => (
  <article className="mb-5 rounded-xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl">
    <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
      <span className="rounded-full border border-red-400/40 bg-red-400/[0.08] px-2 py-0.5 text-red-200">
        {T.psi_error}{item.numero}
      </span>
    </div>
    <h3 className="font-display text-lg font-black text-white">{item.nombre}</h3>
    <p className="mt-2 text-[13px] leading-relaxed text-white/70">{item.descripcion}</p>

    {item.sintomas && (
      <div className="mt-3 rounded-lg border border-red-400/20 bg-red-400/[0.04] p-3">
        <div className="mb-1.5 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-red-200">
          <AlertCircle className="h-3 w-3" />
          {T.psi_symptoms}
        </div>
        <ul className="space-y-0.5 text-[12px] text-white/65">
          {item.sintomas.map((s, i) => (
            <li key={i}>· {s}</li>
          ))}
        </ul>
      </div>
    )}

    {item.solucion && (
      <div className="mt-3 rounded-lg border border-emerald-400/20 bg-emerald-400/[0.04] p-3">
        <div className="mb-1.5 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-200">
          <ShieldCheck className="h-3 w-3" />
          {T.psi_solution}
        </div>
        <p className="text-[12px] leading-relaxed text-white/70">{item.solucion}</p>
      </div>
    )}

    {item.ejercicio && (
      <div className="mt-3 rounded-lg border border-orange-400/20 bg-orange-400/[0.04] p-3">
        <div className="mb-1.5 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-orange-200">
          <Dumbbell className="h-3 w-3" />
          {T.psi_exercise}
        </div>
        <p className="text-[12px] leading-relaxed text-white/70">{item.ejercicio}</p>
      </div>
    )}
  </article>
);

const AcademyPsicologiaDetailPage = () => {
  const { id } = useParams();
  const { lang } = useLanguage();
  const T = getAcademyToolsI18n(lang);
  const l = findLeccionLang(id, lang);
  if (!l) return <Navigate to="/academy/psicologia" replace />;

  const LECCIONES_PSICOLOGIA = getLeccionesPsicologia(lang);
  const idx = LECCIONES_PSICOLOGIA.findIndex((x) => x.id === l.id);
  const prev = idx > 0 ? LECCIONES_PSICOLOGIA[idx - 1] : null;
  const next = idx < LECCIONES_PSICOLOGIA.length - 1 ? LECCIONES_PSICOLOGIA[idx + 1] : null;

  return (
    <AcademyShell
      testid={`academy-psi-${l.id}`}
      eyebrow={`${T.psi_lesson} ${l.numero} · ${l.duracion}`}
      eyebrowIcon={Brain}
      title={l.titulo}
      subtitle={l.descripcion}
      breadcrumbCurrent={l.titulo}
      footHint={T.psi_foot}
    >
      {l.contenido && (
        <div data-testid="psi-detail-errores">
          {l.contenido.map((item, i) => (
            <ErrorBlock key={i} item={item} T={T} />
          ))}
        </div>
      )}

      {l.contenido_texto && (
        <article className="rounded-xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl">
          <pre className="whitespace-pre-wrap font-mono text-[13px] leading-relaxed text-white/75">
            {l.contenido_texto}
          </pre>
        </article>
      )}

      {/* Prev / next nav */}
      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {prev ? (
          <Link
            to={`/academy/psicologia/${prev.id}`}
            className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left hover:border-orange-400/40 hover:bg-orange-400/[0.04]"
          >
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
              {T.psi_prev}
            </div>
            <div className="text-sm font-bold text-white/85">{prev.titulo}</div>
          </Link>
        ) : <span />}
        {next ? (
          <Link
            to={`/academy/psicologia/${next.id}`}
            className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-right hover:border-orange-400/40 hover:bg-orange-400/[0.04]"
          >
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
              {T.psi_next}
            </div>
            <div className="text-sm font-bold text-white/85">{next.titulo}</div>
          </Link>
        ) : <span />}
      </div>
    </AcademyShell>
  );
};

export default AcademyPsicologiaDetailPage;
